import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-07 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/oriora_skein/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device.
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead. Original prototype notes:
//   • "add <brand> [in <colorway>] [fiber X] [<weight>] [N yds] [N skeins]" → add_skein   (local, free)
//   • "queue <name> [<weight>] [N yds]" / "add pattern <name>"             → add_pattern  (local, free)
//   • "start <name>" / "making <name>"                                     → set_pattern_status (local, free)
//   • "mark <name> done" / "finished <name>"                               → set_pattern_status (local, free)
//   • "delete <yarn|pattern> <name>"                                       → delete (HITL confirm)
//   • "recap" / "recap my stash"                                           → recapStash    (the ONE billed call)
//   • "show fingering / partials / my wool / queue / making"               → filter        (local, free)
//   • "summarize / overview"                                               → local stats   (local, free)
//   • anything else / advice-shaped                                        → none + a nudge / the fence
//
// THE FENCE: Skein describes what the user OWNS and does deterministic yardage math — it NEVER
// advises which yarn to buy, ranks a yarn "better", or values yarn for resale. isRefusedQuery()
// runs on the raw message BEFORE serverChat is ever called — an advice-shaped ask is refused for
// FREE, so it never reaches the (billed) routing call either, not just the (billed) recap call
// one step later (which keeps its own fence too, defence in depth).

import { store, YARN_WEIGHTS, weightLabel, COMMON_FIBERS, isPartial, type Skein, type Pattern, type NewSkein, type NewPattern, type PatternStatus, type SkeinPatch, type PatternPatch, type Project, type NewProject, type ProjectPatch } from "../store";
import { BrainError, isRefusedQuery, REFUSAL_MESSAGE, brainCall } from "../brain";
import type { ChatAction, FilterRow } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
export interface BrainResponse {
  reply: string;         // conversational preface (advice is fenced pre-LLM, so the reply is safe to show)
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

const HELP =
  "I can add yarn, queue patterns, mark them making or done, search your stash, summarize, recap, and delete — just tell me. Try:\n" +
  '• "add Brooklyn Tweed in Hawthorne, fingering, 400 yds, 2 skeins"\n' +
  '• "queue Hawthorne cardigan, fingering, 1400 yds"\n' +
  '• "start Hawthorne cardigan" · "mark Hawthorne cardigan done"\n' +
  '• "show fingering" · "show partials" · "show queue" · "summarize" · "recap"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

function esc(s: string): string { return s.replace(/[.*+?^${}()|[\]\\-]/g, "\\$&"); }
function norm(term: string): string { return term.trim().toLowerCase().replace(/[.?!]+$/, ""); }

// Forgiving lookups (FLEET STANDARD, see ./fuzzy): an exact name still wins, otherwise the fuzzy
// resolver tolerates misspellings, dropped words and word order. A skein's identity is brand +
// colorway (adapted to the resolver's `name`); patterns already carry `name`. Client-side only.
async function findSkein(term: string): Promise<Skein[]> {
  const q = norm(term);
  if (!q) return [];
  const all = await store.listSkeins();
  const exact = all.filter((s) => `${s.brand} ${s.colorway}`.trim().toLowerCase() === q || s.colorway.toLowerCase() === q || s.brand.toLowerCase() === q);
  if (exact.length) return exact;
  const named = findNamedMatches(all.map((s) => ({ ...s, name: `${s.brand} ${s.colorway}`.trim() })), term);
  if (named.length) return named;
  return all.filter((s) => s.brand.toLowerCase().includes(q) || s.colorway.toLowerCase().includes(q));
}
async function findPattern(term: string): Promise<Pattern[]> {
  const q = norm(term);
  if (!q) return [];
  const all = await store.listPatterns();
  const exact = all.filter((p) => p.name.toLowerCase() === q);
  if (exact.length) return exact;
  return findNamedMatches(all, term);
}

/** "Couldn't find X" enriched with the closest yarn / pattern names ("did you mean …?"). */
async function skeinsNotFound(term: string): Promise<string> {
  const all = await store.listSkeins();
  return notFoundMessage(all.map((s) => ({ name: `${s.brand}${s.colorway ? ` ${s.colorway}` : ""}`.trim() })), term);
}
async function patternsNotFound(term: string): Promise<string> {
  const all = await store.listPatterns();
  return notFoundMessage(all, term);
}
async function findProject(term: string): Promise<Project[]> {
  const q = norm(term);
  if (!q) return [];
  const all = await store.listProjects();
  const exact = all.filter((p) => p.name.toLowerCase() === q);
  if (exact.length) return exact;
  return findNamedMatches(all, term);
}
async function projectsNotFound(term: string): Promise<string> {
  const all = await store.listProjects();
  return notFoundMessage(all, term);
}
function pickProjects(rows: Project[]): string {
  return rows.slice(0, 5).map((p) => `• ${p.name}`).join("\n");
}

function pickSkeins(rows: Skein[]): string {
  return rows.slice(0, 5).map((s) => `• ${s.brand}${s.colorway ? ` · ${s.colorway}` : ""}`).join("\n");
}
function pickPatterns(rows: Pattern[]): string {
  return rows.slice(0, 5).map((p) => `• ${p.name}`).join("\n");
}

/** Detect + strip a yarn-weight class token from a string. Returns the code + remaining text. */
function grabWeight(rest: string): { weight?: string; rest: string } {
  for (const w of YARN_WEIGHTS) {
    const re = new RegExp(`\\b${esc(w)}\\b`, "i");
    if (re.test(rest)) return { weight: w, rest: rest.replace(re, " ").trim() };
  }
  return { rest };
}

function parseSkein(s: string): NewSkein {
  let rest = s.trim();
  const out: NewSkein = { brand: "" };
  const grab = (re: RegExp): string | undefined => {
    const m = rest.match(re);
    if (m) { rest = rest.replace(m[0], " ").trim(); return m[1].trim(); }
    return undefined;
  };
  out.colorway = grab(/\bcolou?rway\s+([^,]+)/i) ?? grab(/\bin\s+([^,]+)/i);
  // explicit "fiber X" first, else a known common-fiber token
  out.fiber = grab(/\bfiber\s+([^,]+)/i);
  if (!out.fiber) {
    for (const f of COMMON_FIBERS) {
      const re = new RegExp(`\\b${esc(f)}\\b`, "i");
      if (re.test(rest)) { out.fiber = f; rest = rest.replace(re, " ").trim(); break; }
    }
  }
  const yds = grab(/\byardage\s+(\d+)\b/i) ?? grab(/\b(\d+)\s*(?:yds?|yards?)\b/i);
  if (yds) out.yardage_per_skein = parseInt(yds, 10);
  const grams = grab(/\b(\d+)\s*g(?:rams?)?\b/i);
  if (grams) out.grams_per_skein = parseInt(grams, 10);
  const count = grab(/\b(\d+(?:\.\d+)?)\s*skeins?\b/i);
  if (count) out.skein_count = parseFloat(count);
  const w = grabWeight(rest); if (w.weight) out.weight = w.weight; rest = w.rest;
  out.brand = rest.split(",")[0].replace(/\s{2,}/g, " ").trim();
  return out;
}

function parsePattern(s: string): NewPattern {
  let rest = s.trim();
  const out: NewPattern = { name: "" };
  const grab = (re: RegExp): string | undefined => {
    const m = rest.match(re);
    if (m) { rest = rest.replace(m[0], " ").trim(); return m[1].trim(); }
    return undefined;
  };
  const yds = grab(/\byardage\s+(\d+)\b/i) ?? grab(/\bneeds?\s+(\d+)\b/i) ?? grab(/\b(\d+)\s*(?:yds?|yards?)\b/i);
  if (yds) out.total_yardage = parseInt(yds, 10);
  const w = grabWeight(rest); if (w.weight) out.yarn_weight = w.weight; rest = w.rest;
  out.name = rest.split(",")[0].replace(/\s{2,}/g, " ").trim();
  return out;
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  // THE FENCE — pre-LLM, free, on BOTH paths. An advice-shaped ask is refused
  // deterministically before it can reach EITHER billed call (routing OR recap).
  if (isRefusedQuery(text)) { const r = none(REFUSAL_MESSAGE); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (brand/colorway/pattern text → id)
// locally, because the data lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.skein.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_skein", description: "Add a yarn line to the stash.", params: { brand: "REQUIRED the yarn brand", colorway: "optional colorway name", fiber: "optional fiber content", weight: "optional weight class: lace, fingering, sport, dk, worsted, aran, bulky, super-bulky, or jumbo", yardage_per_skein: "optional yards per skein", grams_per_skein: "optional grams per skein", skein_count: "optional number of skeins" } },
  { name: "add_pattern", description: "Queue a pattern the user wants to make.", params: { name: "REQUIRED the pattern name", yarn_weight: "optional weight class it calls for", total_yardage: "optional total yards the pattern needs" } },
  { name: "set_pattern_status", description: "Mark a queued pattern as making (started) or done (finished). Identify it by name.", params: { name: "REQUIRED — which pattern, name text as the user wrote it", status: '"making" or "done"' } },
  { name: "edit_skein", description: "Change fields on an existing yarn line (brand, colorway, fiber, weight, yardage/grams per skein, skein count, or notes). Identify it by brand or colorway. Give at least one field.", params: { yarn: "REQUIRED — which yarn, brand or colorway as the user wrote it", brand: "new brand, only if changing", colorway: "new colorway, only if changing", fiber: "new fiber, only if changing", weight: "new weight class, only if changing", yardage_per_skein: "new yards per skein, only if changing", grams_per_skein: "new grams per skein, only if changing", skein_count: "new skein count, only if changing", notes: "new notes, only if changing" } },
  { name: "edit_pattern", description: "Change fields on an existing pattern (name, yarn weight, total yardage, needle size, source, or notes). Identify it by name. For only status use set_pattern_status. Give at least one field.", params: { name: "REQUIRED — which pattern, name as the user wrote it", new_name: "new name, only if renaming", yarn_weight: "new weight class, only if changing", total_yardage: "new total yards, only if changing", needle_size: "new needle size, only if changing", source: "new source, only if changing", notes: "new notes, only if changing" } },
  { name: "delete_skein", description: "Delete one yarn line identified by brand or colorway (the app asks the user to confirm first).", params: { yarn: "which yarn — brand or colorway text as the user wrote it" } },
  { name: "delete_pattern", description: "Delete one queued pattern identified by name (the app asks the user to confirm first).", params: { name: "which pattern — name text as the user wrote it" } },
  { name: "filter_skein", description: "Search / list yarn in the stash.", params: { weight: "optional weight class to filter by", partial: "optional true to show only partially-used skeins", text: "optional free text to search brand/colorway/fiber" } },
  { name: "filter_pattern", description: "Search / list queued patterns.", params: { status: '"queue" (not yet started), "making", or "done"' } },
  { name: "add_project", description: "Log a finished project (the Projects tab record of what the user has made).", params: { name: "REQUIRED — the project name", yarn_weight: "optional weight class used", yardage_used: "optional total yards used", finished_on: "optional YYYY-MM-DD it was finished", notes: "optional note" } },
  { name: "edit_project", description: "Change fields on a finished project (name, yarn weight, yardage used, finished date, or notes). Identify it by name. Give at least one field.", params: { name: "REQUIRED — which project, name as the user wrote it", new_name: "new name, only if renaming", yarn_weight: "new weight, only if changing", yardage_used: "new yards used, only if changing", finished_on: "new YYYY-MM-DD, only if changing", notes: "new notes, only if changing" } },
  { name: "delete_project", description: "Delete one finished project by its name (the app asks the user to confirm first).", params: { name: "which project — name as the user wrote it" } },
  { name: "filter_project", description: "Search / list finished projects.", params: { text: "optional free text to search project names" } },
  { name: "counter_new", description: "Start a new row counter (knitting/crochet). Use for 'start a counter', 'new row counter called sleeves'.", params: { name: "optional counter name, e.g. 'Rows' or 'Sleeve'", step: "optional amount each tap adds (default 1)" } },
  { name: "counter_bump", description: "Add rows to a row counter. Use for 'add 3 rows', 'count 5 on the sleeve counter', 'I did 10 rows'. If the user names a counter, pass it; otherwise it bumps the only/first counter.", params: { name: "optional which counter, by name", by: "optional how many rows to add (default the counter's step)" } },
  { name: "counter_reset", description: "Reset a row counter to 0. Use for 'reset my counter', 'start the sleeve count over'.", params: { name: "optional which counter, by name" } },
  { name: "recap", description: "Narrate the stash: usable yardage by weight, pattern surplus/shortfall, dye-lot risk, fiber mix, idle stash — computed on-device, observational only. Never advises which yarn to buy, ranks yarn as better, or values yarn for resale. No params.", params: {} },
  { name: "summarize", description: "Quick stash stats: yarn lines, usable yards, partials, queued/making counts, finished projects — computed on-device. No params.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "save_to_cloud", description: "Save a NOTE or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their messages, or a piece of text. (To back up the WHOLE stash + queue instead, use backup_save.)", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const [skeins, patterns] = await Promise.all([store.listSkeins(), store.listPatterns()]);
  const yarns = skeins.slice(0, 30).map((s) => `${s.brand}${s.colorway ? ` (${s.colorway})` : ""}`).join(", ");
  const names = patterns.slice(0, 30).map((p) => p.name).join(", ");
  const _notes = getInstructions();
  const context = (`LIBRARY (their saved plans/notes): ${librarySummary()}. Stash yarns: ${yarns || "(none yet)"}. Queued/making patterns: ${names || "(none yet)"}.`)
    + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);

  // The shared brain returns an ARRAY (G17): "add yarn X and queue pattern Y" = two actions.
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asBool = (v: unknown): boolean | undefined => (typeof v === "boolean" ? v : undefined);
const asInt = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= lo && n <= hi ? Math.round(n) : undefined;
};
const asFloat = (v: unknown, lo: number, hi: number): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n >= lo && n <= hi ? n : undefined;
};

export async function toAction(r: ServerChat): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_skein": {
      const brand = asStr(p.brand);
      if (!brand) return none(say || 'What yarn? Try "add Brooklyn Tweed in Hawthorne, fingering, 400 yds".');
      const input: NewSkein = { brand };
      const colorway = asStr(p.colorway);
      if (colorway) input.colorway = colorway;
      const fiber = asStr(p.fiber);
      if (fiber) input.fiber = fiber;
      const weight = asStr(p.weight)?.toLowerCase();
      if (weight && (YARN_WEIGHTS as readonly string[]).includes(weight)) input.weight = weight;
      const yardage = asInt(p.yardage_per_skein, 1, 100000);
      if (yardage != null) input.yardage_per_skein = yardage;
      const grams = asInt(p.grams_per_skein, 1, 100000);
      if (grams != null) input.grams_per_skein = grams;
      const count = asFloat(p.skein_count, 0.1, 1000);
      if (count != null) input.skein_count = count;
      return { reply: say, action: { kind: "add_skein", input } };
    }
    case "add_pattern": {
      const name = asStr(p.name);
      if (!name) return none(say || 'What\'s the pattern called? Try "queue Hawthorne cardigan, fingering, 1400 yds".');
      const input: NewPattern = { name };
      const yw = asStr(p.yarn_weight)?.toLowerCase();
      if (yw && (YARN_WEIGHTS as readonly string[]).includes(yw)) input.yarn_weight = yw;
      const yds = asInt(p.total_yardage, 1, 1000000);
      if (yds != null) input.total_yardage = yds;
      return { reply: say, action: { kind: "add_pattern", input } };
    }
    case "set_pattern_status": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which pattern? Give me its name.");
      const statusWord = asStr(p.status)?.toLowerCase();
      const status: PatternStatus = statusWord === "done" || statusWord === "finished" || statusWord === "complete" ? "done" : "making";
      const rows = await findPattern(term);
      if (rows.length === 0) return none(await patternsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} patterns matching "${term}" — be more specific:\n${pickPatterns(rows)}`);
      return { reply: say, action: { kind: "set_pattern_status", id: rows[0].id, status, label: `${rows[0].name} → ${status}` } };
    }
    case "edit_skein": {
      const term = asStr(p.yarn);
      if (!term) return none(say || "Which yarn should I update? Give me its brand or colorway.");
      const rows = await findSkein(term);
      if (rows.length === 0) return none(await skeinsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} yarns matching "${term}" — be more specific:\n${pickSkeins(rows)}`);
      const patch: SkeinPatch = {};
      const bits: string[] = [];
      const brand = asStr(p.brand); if (brand) { patch.brand = brand; bits.push(`brand → ${brand}`); }
      const colorway = asStr(p.colorway); if (colorway) { patch.colorway = colorway; bits.push(`colorway → ${colorway}`); }
      const fiber = asStr(p.fiber); if (fiber) { patch.fiber = fiber; bits.push(`fiber → ${fiber}`); }
      const weight = asStr(p.weight)?.toLowerCase();
      if (weight && (YARN_WEIGHTS as readonly string[]).includes(weight)) { patch.weight = weight; bits.push(`weight → ${weightLabel(weight)}`); }
      const yardage = asInt(p.yardage_per_skein, 1, 100000); if (yardage != null) { patch.yardage_per_skein = yardage; bits.push(`${yardage} yds/skein`); }
      const grams = asInt(p.grams_per_skein, 1, 100000); if (grams != null) { patch.grams_per_skein = grams; bits.push(`${grams} g/skein`); }
      const count = asFloat(p.skein_count, 0.1, 1000); if (count != null) { patch.skein_count = count; bits.push(`${count} skeins`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on that yarn?");
      const c = rows[0];
      return { reply: say, action: { kind: "edit_skein", id: c.id, patch, label: `${c.brand}${c.colorway ? ` · ${c.colorway}` : ""}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "edit_pattern": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which pattern should I update? Give me its name.");
      const rows = await findPattern(term);
      if (rows.length === 0) return none(await patternsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} patterns matching "${term}" — be more specific:\n${pickPatterns(rows)}`);
      const patch: PatternPatch = {};
      const bits: string[] = [];
      const newName = asStr(p.new_name); if (newName) { patch.name = newName; bits.push(`name → ${newName}`); }
      const yw = asStr(p.yarn_weight)?.toLowerCase();
      if (yw && (YARN_WEIGHTS as readonly string[]).includes(yw)) { patch.yarn_weight = yw; bits.push(`weight → ${weightLabel(yw)}`); }
      const yds = asInt(p.total_yardage, 1, 1000000); if (yds != null) { patch.total_yardage = yds; bits.push(`${yds} yds`); }
      const needle = asStr(p.needle_size); if (needle) { patch.needle_size = needle; bits.push(`needle → ${needle}`); }
      const source = asStr(p.source); if (source) { patch.source = source; bits.push(`source → ${source}`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on that pattern?");
      return { reply: say, action: { kind: "edit_pattern", id: rows[0].id, patch, label: `${rows[0].name}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_skein": {
      const term = asStr(p.yarn);
      if (!term) return none(say || "Which yarn do you want to delete? Give me its brand or colorway.");
      const rows = await findSkein(term);
      if (rows.length === 0) return none(await skeinsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} yarns matching "${term}" — be more specific:\n${pickSkeins(rows)}`);
      return { reply: say, action: { kind: "delete_skein", id: rows[0].id, label: `${rows[0].brand}${rows[0].colorway ? ` · ${rows[0].colorway}` : ""}` } };
    }
    case "delete_pattern": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which pattern do you want to delete? Give me its name.");
      const rows = await findPattern(term);
      if (rows.length === 0) return none(await patternsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} patterns matching "${term}" — be more specific:\n${pickPatterns(rows)}`);
      return { reply: say, action: { kind: "delete_pattern", id: rows[0].id, label: rows[0].name } };
    }
    case "filter_skein": {
      const weight = asStr(p.weight)?.toLowerCase();
      const partial = asBool(p.partial);
      const text = asStr(p.text)?.toLowerCase();
      const pred = (s: Skein): boolean => {
        if (weight && (YARN_WEIGHTS as readonly string[]).includes(weight) && s.weight !== weight) return false;
        if (partial === true && !isPartial(s)) return false;
        if (text && !(s.brand.toLowerCase().includes(text) || s.colorway.toLowerCase().includes(text) || s.fiber.toLowerCase().includes(text))) return false;
        return true;
      };
      const label = weight ? weightLabel(weight) : (text || undefined);
      return { reply: "", action: { kind: "filter_skein", pred, label } };
    }
    case "filter_pattern": {
      const status = asStr(p.status)?.toLowerCase();
      let pred: (pt: Pattern) => boolean;
      let label: string;
      if (status === "making") { pred = (pt) => pt.status === "making"; label = "making"; }
      else if (status === "done" || status === "finished") { pred = (pt) => pt.status === "done"; label = "done patterns"; }
      else { pred = (pt) => pt.status !== "done"; label = "queue"; }
      return { reply: "", action: { kind: "filter_pattern", pred, label } };
    }
    case "add_project": {
      const name = asStr(p.name);
      if (!name) return none(say || "What's the project called?");
      const input: NewProject = { name };
      const yw = asStr(p.yarn_weight)?.toLowerCase();
      if (yw && (YARN_WEIGHTS as readonly string[]).includes(yw)) input.yarn_weight = yw;
      const yds = asInt(p.yardage_used, 1, 1000000); if (yds != null) input.yardage_used = yds;
      const fin = asStr(p.finished_on)?.match(/\d{4}-\d{2}-\d{2}/)?.[0]; if (fin) input.finished_on = fin;
      const notes = asStr(p.notes); if (notes) input.notes = notes;
      return { reply: say, action: { kind: "add_project", input } };
    }
    case "edit_project": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which project should I update? Give me its name.");
      const rows = await findProject(term);
      if (rows.length === 0) return none(await projectsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} projects matching "${term}" — be more specific:\n${pickProjects(rows)}`);
      const patch: ProjectPatch = {};
      const bits: string[] = [];
      const newName = asStr(p.new_name); if (newName) { patch.name = newName; bits.push(`name → ${newName}`); }
      const yw = asStr(p.yarn_weight)?.toLowerCase();
      if (yw && (YARN_WEIGHTS as readonly string[]).includes(yw)) { patch.yarn_weight = yw; bits.push(`weight → ${weightLabel(yw)}`); }
      const yds = asInt(p.yardage_used, 1, 1000000); if (yds != null) { patch.yardage_used = yds; bits.push(`${yds} yds`); }
      const fin = asStr(p.finished_on)?.match(/\d{4}-\d{2}-\d{2}/)?.[0]; if (fin) { patch.finished_on = fin; bits.push(`finished ${fin}`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on that project?");
      return { reply: say, action: { kind: "edit_project", id: rows[0].id, patch, label: `${rows[0].name}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_project": {
      const term = asStr(p.name);
      if (!term) return none(say || "Which project do you want to delete? Give me its name.");
      const rows = await findProject(term);
      if (rows.length === 0) return none(await projectsNotFound(term));
      if (rows.length > 1) return none(`I found ${rows.length} projects matching "${term}" — be more specific:\n${pickProjects(rows)}`);
      return { reply: say, action: { kind: "delete_project", id: rows[0].id, label: rows[0].name } };
    }
    case "filter_project": {
      const text = asStr(p.text)?.toLowerCase();
      const pred = (pr: Project): boolean => !text || pr.name.toLowerCase().includes(text);
      return { reply: "", action: { kind: "filter_project", pred, label: text || undefined } };
    }
    case "counter_new":
      return { reply: say, action: { kind: "counter_new", name: asStr(p.name), step: asInt(p.step, 1, 999) } };
    case "counter_bump":
      return { reply: say, action: { kind: "counter_bump", name: asStr(p.name), by: asInt(p.by, 1, 9999) } };
    case "counter_reset":
      return { reply: say, action: { kind: "counter_reset", name: asStr(p.name) } };
    case "recap":
      return { reply: say, action: { kind: "recap" } };
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? asStr(p.contentKind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest === "github" ? "github" : dest === "gdrive" ? "gdrive" : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 0) FENCE — never let the chat give buying advice or value yarn for resale (reuses brain.ts).
  if (isRefusedQuery(t)) return { reply: "", action: { kind: "none", message: REFUSAL_MESSAGE } };

  // 1) SUMMARIZE — local stats (free).
  if (/\b(summar(?:y|ise|ize)|overview|how\s+am\s+i\s+doing)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 2) RECAP — the ONE billed call (whole-stash, deterministic signals).
  if (/^recap\b/i.test(low) || /\brecap\s+(?:my\s+|the\s+)?stash\b/i.test(low)) {
    return { reply: "", action: { kind: "recap" } };
  }

  // 3) DELETE — resolve locally; the UI asks for confirmation before executing.
  let m = t.match(/^(?:delete|remove)\s+(?:the\s+)?(yarn|skein|pattern)?\s*(.+)$/i);
  if (m) {
    const scope = (m[1] || "").toLowerCase();
    const term = m[2];
    if (scope === "pattern") {
      const rows = await findPattern(term);
      if (rows.length === 0) return { reply: "", action: { kind: "none", message: await patternsNotFound(term) } };
      if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} patterns matching "${term.trim()}" — be more specific:\n${pickPatterns(rows)}` } };
      return { reply: "", action: { kind: "delete_pattern", id: rows[0].id, label: rows[0].name } };
    }
    // default / "yarn"/"skein": try skeins, then patterns as a fallback.
    const sk = await findSkein(term);
    if (sk.length === 1) return { reply: "", action: { kind: "delete_skein", id: sk[0].id, label: `${sk[0].brand}${sk[0].colorway ? ` · ${sk[0].colorway}` : ""}` } };
    if (sk.length > 1) return { reply: "", action: { kind: "none", message: `I found ${sk.length} yarns matching "${term.trim()}" — be more specific:\n${pickSkeins(sk)}` } };
    if (scope === "" ) {
      const pt = await findPattern(term);
      if (pt.length === 1) return { reply: "", action: { kind: "delete_pattern", id: pt[0].id, label: pt[0].name } };
      if (pt.length > 1) return { reply: "", action: { kind: "none", message: `I found ${pt.length} patterns matching "${term.trim()}" — be more specific:\n${pickPatterns(pt)}` } };
    }
    return { reply: "", action: { kind: "none", message: `I couldn't find anything matching "${term.trim()}".` } };
  }

  // 4) PATTERN → DONE — "mark X done" / "finished X" / "X is done".
  m = t.match(/^(?:mark|set)\s+(.+?)\s+(?:as\s+)?(?:done|finished|complete|completed)\s*$/i)
    || t.match(/^(?:finished|completed|done)\s+(.+)$/i)
    || t.match(/^(.+?)\s+is\s+(?:done|finished|complete)\s*$/i);
  if (m) {
    const rows = await findPattern(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await patternsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} patterns matching "${m[1].trim()}" — be more specific:\n${pickPatterns(rows)}` } };
    return { reply: "", action: { kind: "set_pattern_status", id: rows[0].id, status: "done", label: `${rows[0].name} → done` } };
  }

  // 5) PATTERN → MAKING — "start X" / "casting on X" / "making X" / "mark X making".
  m = t.match(/^(?:start|starting|begin|making|knitting|crocheting|cast\s*on|casting\s*on)\s+(.+)$/i)
    || t.match(/^(?:mark|set)\s+(.+?)\s+(?:as\s+)?making\s*$/i);
  if (m) {
    const rows = await findPattern(m[1]);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await patternsNotFound(m[1]) } };
    if (rows.length > 1) return { reply: "", action: { kind: "none", message: `I found ${rows.length} patterns matching "${m[1].trim()}" — be more specific:\n${pickPatterns(rows)}` } };
    return { reply: "", action: { kind: "set_pattern_status", id: rows[0].id, status: "making", label: `${rows[0].name} → making` } };
  }

  // 6) ADD PATTERN — "queue X" / "add pattern X".
  m = t.match(/^(?:add\s+pattern|queue(?:\s+up)?|add\s+to\s+(?:the\s+)?queue)\s+(.+)$/i);
  if (m) {
    const input = parsePattern(m[1]);
    if (!input.name) return { reply: "", action: { kind: "none", message: "What's the pattern called? Try \"queue Hawthorne cardigan, fingering, 1400 yds\"." } };
    return { reply: "", action: { kind: "add_pattern", input } };
  }

  // 7) ADD YARN — light local parse (no server call).
  m = t.match(/^(?:add|log|new|track)\s+(?:a\s+|an\s+|some\s+)?(?:yarn|skein|skeins)?\s*(.+)$/i);
  if (m) {
    const input = parseSkein(m[1]);
    if (!input.brand) return { reply: "", action: { kind: "none", message: "What yarn? Try \"add Brooklyn Tweed in Hawthorne, fingering, 400 yds\"." } };
    return { reply: "", action: { kind: "add_skein", input } };
  }

  // 8) FILTER / SEARCH — local predicate over the stash or the queue.
  const pf = buildPatternFilter(low);
  if (pf) return { reply: "", action: { kind: "filter_pattern", pred: pf.pred, label: pf.label } };
  const sf = buildSkeinFilter(t, low);
  if (sf) return { reply: "", action: { kind: "filter_skein", pred: sf.pred, label: sf.label } };

  // 9) Unclear → a short nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

function buildPatternFilter(low: string): { pred: (p: Pattern) => boolean; label: string } | null {
  if (/\b(queue|queued)\b/i.test(low)) return { pred: (p) => p.status !== "done", label: "queue" };
  if (/\b(making|in\s*progress|on\s+the\s+needles|casting\s*on)\b/i.test(low)) return { pred: (p) => p.status === "making", label: "making" };
  if (/\b(done|finished)\s+patterns?\b/i.test(low)) return { pred: (p) => p.status === "done", label: "done patterns" };
  return null;
}

function buildSkeinFilter(t: string, low: string): { pred: (s: Skein) => boolean; label: string } | null {
  if (/\bpartials?\b/i.test(low)) return { pred: (s) => isPartial(s), label: "partials" };
  // weight class ("show fingering", "show my dk")
  for (const w of YARN_WEIGHTS) {
    if (new RegExp(`\\b${esc(w)}\\b`, "i").test(low)) return { pred: (s) => s.weight === w, label: weightLabel(w) };
  }
  // "show/find <term>" → match by brand, colorway, or fiber.
  const m = t.match(/^(?:show|find|list|search)\s+(?:me\s+)?(?:all\s+)?(?:my\s+)?(.+)$/i);
  if (m) {
    const q = norm(m[1]);
    if (!q) return null;
    return {
      pred: (s) => s.brand.toLowerCase().includes(q) || s.colorway.toLowerCase().includes(q) || s.fiber.toLowerCase().includes(q),
      label: q,
    };
  }
  return null;
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
